import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomFieldsValueI } from '@interfaces/custom-fields.interface';
import { environment } from '@environments/environment';
import { ContactI, ContactMergeHistoryEntryI } from '@interfaces/contact.interface';
import { PageI } from '@interfaces/page.interface';
import { Observable, firstValueFrom } from 'rxjs';
import { ContactsFilterI } from '@interfaces/contacts-filter.interface';
import { DateTimeHelper } from '@helpers/date-time-helper.service';
import { UpdateMultipleContactTagI } from '@interfaces/update-tag.interface';
import { FileImportMetaT } from '@omnitypes/file-import/file-import';
import { ContactUploadMetadataI } from '@interfaces/contact-upload-metadata.interface';

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  private baseUrl = environment.OMNISMART_API_CONTACTS;
  private slug_url = 'contacts';
  private attendanceReportsUrl = environment.OMNISMART_API_ATTENDANCE_REPORTS;
  private contactsExportSlug = 'contacts-export';

  constructor(private http: HttpClient, private timeHelper: DateTimeHelper) {}

  public getContacts(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 99999,
    active = 'createdAt',
    status: string | 'active' | 'inactive' = '',
    hideGroup = false,
    populateQuery = false,
    filters?: ContactsFilterI
  ): Observable<PageI<ContactI>> {
    let params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('status', status);

    if (hideGroup) {
      params = params.set('hide-contact-from-group', hideGroup);
    }

    if (populateQuery) {
      params = params.set('populate-query', populateQuery);
    }

    if (filters?.contacts) {
      params = params.set('contacts', filters.contacts.toString());
    }

    if (filters?.createdAt) {
      params = params.set(
        'createdAt',
        [
          this.timeHelper.startOfDay(filters.createdAt.start.toDate()).toISOString(),
          this.timeHelper.endOfDay(filters.createdAt.end.toDate()).toISOString(),
        ].toString()
      );
    }

    if (filters?.users) {
      params = params.set('users', filters.users.toString());
    }

    if (filters?.source) {
      params = params.set('source', filters.source.toString());
    }

    if (filters?.tags) {
      params = params.set('tags', filters.tags.toString());
    }

    return this.http.get<PageI<ContactI>>(`${this.baseUrl}${this.slug_url}`, {
      params,
    });
  }

  public getAllContacts(): Observable<PageI<ContactI>> {
    const params = new HttpParams().set('limit', '9999');
    return this.http.get<PageI<ContactI>>(`${this.baseUrl}${this.slug_url}`, {
      params,
    });
  }

  public getContactByPhone(telephone: string, customer: string): Observable<ContactI> {
    const params = new HttpParams().set('customer', customer);
    return this.http.get<ContactI>(`${this.baseUrl}${this.slug_url}/find-by-phone/${telephone}`, { params });
  }
  public downloadLocalFile(fileName: string) {
    return firstValueFrom(
      this.http.get(fileName, {
        responseType: 'blob',
      })
    );
  }

  public getContactById(id: string): Observable<ContactI> {
    return this.http.get<ContactI>(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public async updateContact(contact: ContactI): Promise<ContactI> {
    return firstValueFrom(this.http.patch<ContactI>(`${this.baseUrl}${this.slug_url}/${contact._id}`, contact));
  }

  public async createContact(contact: ContactI): Promise<ContactI> {
    const body = Object.assign({}, contact);
    delete body['_id'];
    return firstValueFrom(this.http.post<ContactI>(`${this.baseUrl}${this.slug_url}`, body));
  }

  public mergeContacts(data: {
    origin: ContactI;
    destiny: ContactI;
    typeMerge?: string;
    overrides?: Partial<ContactI>;
    customFieldsOverride?: Array<Partial<CustomFieldsValueI>>;
    choices?: Record<string, 'origin' | 'destiny' | 'union'>;
  }): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}${this.slug_url}/merge`, data);
  }

  public getMergeHistory(contactId: string): Observable<ContactMergeHistoryEntryI[]> {
    return this.http.get<ContactMergeHistoryEntryI[]>(`${this.baseUrl}${this.slug_url}/${contactId}/merge-history`);
  }

  public deleteContact(id: string): Observable<ContactI> {
    return this.http.delete<ContactI>(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public bulkDelete(ids: string[]): Observable<ContactI> {
    return this.http.delete<ContactI>(`${this.baseUrl}${this.slug_url}/bulk-delete`, { body: ids });
  }

  public countContacts(): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}${this.slug_url}/count`);
  }

  public getAddressByCep(cep: string): Observable<any> {
    return this.http.get<any>(`https://viacep.com.br/ws/${cep}/json/`, {
      headers: {
        'content-type': 'application/json;charset=utf-8',
      },
    });
  }

  public importContacts(instance: string[]): Observable<any> {
    return this.http.post<any>(`${environment.OMNISMART_API_WHATSAPP_OMNI}webhook-provider/import-contacts`, instance);
  }

  // async export: hits attendance-reports which processes in background and
  // returns a jobId; the file arrives via socket (download history / bell).
  // avoids the timeout/OOM the synchronous contacts export hit on large bases.
  exportContactsCSV(selectedIds?: string[]) {
    return this.http.get<{ jobId: string }>(`${this.attendanceReportsUrl}${this.contactsExportSlug}/csv/exports`, {
      params: this.buildExportParams(selectedIds),
    });
  }

  exportContactsTxt(selectedIds?: string[]) {
    return this.http.get<{ jobId: string }>(`${this.attendanceReportsUrl}${this.contactsExportSlug}/txt/exports`, {
      params: this.buildExportParams(selectedIds),
    });
  }

  exportContactsExcel(selectedIds?: string[]) {
    return this.http.get<{ jobId: string }>(`${this.attendanceReportsUrl}${this.contactsExportSlug}/excel/exports`, {
      params: this.buildExportParams(selectedIds),
    });
  }

  private buildExportParams(selectedIds?: string[]): HttpParams {
    let params = new HttpParams();
    if (selectedIds?.length) {
      params = params.set('selectedIds', selectedIds.toString());
    }
    return params;
  }

  public updateContactTag(contactId: string, tags: object[]): Observable<any> {
    return this.http.patch(this.baseUrl + this.slug_url + '/update-tag/' + contactId, {
      tags,
    });
  }

  public updateContactsTag(body: UpdateMultipleContactTagI): Observable<any> {
    return this.http.patch(this.baseUrl + this.slug_url + '/update-tag', body);
  }

  public createCustomField(field: CustomFieldsValueI) {
    const url = `${this.baseUrl}${this.slug_url}/custom-field`;
    return this.http.post<CustomFieldsValueI>(url, field);
  }

  public updateCustomField(id: string, field: CustomFieldsValueI) {
    const url = `${this.baseUrl}${this.slug_url}/custom-field/${id}`;
    return this.http.patch<CustomFieldsValueI>(url, field);
  }

  public getCustomerCustomFields(): Observable<CustomFieldsValueI[]> {
    return this.http.get<CustomFieldsValueI[]>(`${this.baseUrl}${this.slug_url}/custom-field`);
  }

  public deleteCustomField(id: string) {
    return this.http.delete<CustomFieldsValueI>(`${this.baseUrl}${this.slug_url}/custom-field/${id}`);
  }

  public updateMultipleRequiredCustomFields(customFields: CustomFieldsValueI[]): Observable<CustomFieldsValueI[]> {
    return this.http.patch<CustomFieldsValueI[]>(
      `${this.baseUrl}${this.slug_url}/custom-field/multi-update-required`,
      customFields
    );
  }

  public getUploadStatus() {
    return this.http.get<ContactUploadMetadataI | undefined>(`${this.baseUrl}${this.slug_url}/upload-status`);
  }

  public findOrCreateContactForRubeusIntegration(parameterId: string, rubeusContactId: string) {
    return this.http.get<ContactI>(`${this.baseUrl}${this.slug_url}/rubeus-contact/${parameterId}/${rubeusContactId}`);
  }

  public async findOrCreateContactByPhone(phone: string, body: Partial<ContactI>): Promise<ContactI> {
    return firstValueFrom(
      this.http.post<ContactI>(`${this.baseUrl}${this.slug_url}/find-by-phone-or-create/${phone}`, body)
    );
  }

  public async updateContactByPhone(phone: string, body: Partial<ContactI>): Promise<ContactI> {
    return firstValueFrom(this.http.post<ContactI>(`${this.baseUrl}${this.slug_url}/update-by-phone/${phone}`, body));
  }

  public importUpload(file: File): Observable<FileImportMetaT | undefined> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<FileImportMetaT | undefined>(`${this.baseUrl + this.slug_url}/import/upload`, formData);
  }

  public importCommit(token: string, customFields?: Record<string, string>) {
    return this.http.post<any>(`${this.baseUrl + this.slug_url}/import/commit/${token}`, {
      customFields: JSON.stringify(customFields),
    });
  }

  public importErrors(token: string) {
    return this.http.get<any>(`${this.baseUrl + this.slug_url}/import/errors/${token}`);
  }
}
